import React, { useEffect, useRef, useState, useMemo } from 'react'
import ApiService from '../../ApiService'
import { eligibleState, getStates, validZipCode } from '../../config/config'
import { useHistory, useLocation, Link } from 'react-router-dom'
import PassMemberTermsModal from '../signup/PassMemberTermsModal'
import MuiAlert from '@material-ui/lab/Alert'
import Snackbar from '@material-ui/core/Snackbar'
import { DateTime } from 'luxon'
import useUserStore from '../../hook/useUserStore'
import { subYears } from 'date-fns'
import './style.css'
import DatePicker from 'react-datepicker'
import Badge from 'react-bootstrap/Badge'
import Overlay from 'react-bootstrap/Overlay'
import Tooltip from 'react-bootstrap/Tooltip'
import { getPreparedPhoneNumber, validPhoneNumber } from '../../util/UtilFunctions'
import { addressedAtOptions, sexOptions, identity } from '../../util/common'

const states = getStates()

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const HealthProfile = props => {
  const history = useHistory()
  const [snackOpen, setSnackOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [fname, setFname] = useState('')
  const [lname, setLname] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [streetAddress, setStreetAddress] = useState('')
  const [streetAddress2, setStreetAddress2] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [allowedState, setAllowedState] = useState(true)
  const [zip, setZip] = useState('')
  const [validZip, setValidZip] = useState('')
  const [dob, setDob] = useState(null)
  const [tooYoung, setTooYoung] = useState(false)
  const [gender, setGender] = useState('')
  const [errors, setErrors] = useState([])
  const [allergies, setAllergies] = useState('')
  const [race, setRaceValue] = useState('Unknown')
  const [ethnicity, setEthnicityValue] = useState('Unknown')
  const [symptoms, setSymptomsValue] = useState(null)
  const [preExisting, setPreExisting] = useState(null)
  const [medications, setMedicationsValue] = useState(null)
  const [pronoun, setPronounValue] = useState('')
  const [isPassMember, setPassMemberStatus] = useState(false)
  const [passMemberAck, setPassmemberAck] = useState(false)
  const [shareToPass, setShareToPass] = useState(false)
  const [preferredName, setPreferredName] = useState('')
  const errorRef = useRef(null)

  const [sexAtBirth, setSexAtBirth] = useState(null)
  const [pronouns, setPronouns] = useState(null)
  const [genderIdentity, setGenderIdentity] = useState(null)

  const location = useLocation()
  const nextPath = new URLSearchParams(location.search).get('next')
  const missingInfo = new URLSearchParams(location.search).get('missing-info')

  const ethnicityList = [
    { label: 'True', value: 'Hispanic or Latino', key: 'hispani' },
    { label: 'False', value: 'not Hispanic or Latino', key: 'not-hispani' },
    { label: 'Decline to State', value: 'Unknown', key: 'unknown' },
  ]

  const patient = useUserStore(state => state.patient)
  const setPatient = useUserStore(state => state.setPatient)

  const isProfileIncomplete = useMemo(() => {
    return !patient?.genderAtBirth || !patient?.pronoun || !patient?.currentGenderIdentity || !patient?.medication
  }, [patient])

  document.body.classList.add('body_portal')

  useEffect(() => {
    if (!errors?.length) return
    errorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errors])

  useEffect(() => {
    if (missingInfo === 'true' || missingInfo === true) {
      setSnackOpen(true)
    }
    if (!patient) return

    setPhoneNumber(patient.phoneNumber ?? '')
    setFname(patient.firstName ?? '')
    setLname(patient.lastName ?? '')
    setGender(patient.gender ?? '')
    if (patient.dob && patient.dob?.trim() !== '') {
      setDob(DateTime.fromISO(patient.dob).toJSDate())
    }

    setAllergies(patient.allergies ?? '')
    setEthnicityValue(patient.ethnicity ?? '')
    setRaceValue(patient.race ?? '')
    setSymptomsValue(patient.symptoms ?? '')
    setPreExisting(patient.preExisting ?? '')
    setMedicationsValue(patient.medication ?? '')
    setPronounValue(patient.pronoun ?? '')
    setSexAtBirth(patient.genderAtBirth ?? '')
    setPronouns(patient.pronoun ?? '')
    setGenderIdentity(patient.currentGenderIdentity ?? '')
    setPassmemberAck(patient?.isPassMember === 'true' || patient?.isPassMember === true ? true : false)
    setShareToPass(patient?.shareToPass === 'true' || patient?.shareToPass === true ? true : false)
    setPreferredName(patient.preferredName ?? '')

    const { address } = patient
    setStreetAddress(address?.street_address ?? '')
    setStreetAddress2(address?.street_address2 ?? '')
    setCity(address?.locality ?? '')
    setState(address?.region ?? '')
    setZip(address?.postal_code ?? '')
    validZipCode(address?.postal_code && address?.postal_code?.trim() !== '')
    setValidZip(address?.postal_code ?? '')
  }, [])

  const ethnicityFormGroup = []
  ethnicityList.forEach(et => {
    ethnicityFormGroup.push(
      <label for={et.key} class="radio-input">
        <input type="radio" id={et.key} name="ethnicityInput" checked={et.value === ethnicity} value={et.value} onChange={e => setEthnicityValue(e.target.value)} />
        <span>{et.label}</span>
      </label>
    )
  })
  const raceList = [
    { label: 'American Indian or Alaska Native', key: 'alaska', value: 'American Indian or Alaska Native' },
    { label: 'Asian', value: 'Asian', key: 'asian' },
    {
      label: 'Native Hawaiian or Other Pacific Islander',
      key: 'Hawaiian',
      value: 'Native Hawaiian or Other Pacific Islander',
    },
    { label: 'Black or African-American', key: 'africa', value: 'Black or African-American' },
    { label: 'White', value: 'White', key: 'white' },
    { label: 'Other', value: 'Other', key: 'other' },
    { label: 'Decline to State', value: 'Unknown', key: 'unknown', checked: true },
  ]
  const raceFormGroup = []
  raceList.forEach(et => {
    raceFormGroup.push(
      <label for={et.key} class="radio-input">
        <input type="radio" id={et.key} name="raceInput" checked={et.value === race} value={et.value} onChange={e => setRaceValue(e.target.value)} />
        <span>{et.label}</span>
      </label>
    )
  })

  document.body.classList.add('body_login')

  const handleStateChange = event => {
    setAllowedState(eligibleState(event.target.value))
    setState(event.target.value)
  }

  const handleZipChange = event => {
    setValidZip(validZipCode(state, event.target.value))
    setValidZip(true)
    setZip(event.target.value.trim())
  }

  const backButton = () => {
    if (page > 1 && page <= 3) {
      setPage(page - 1)
    }
  }

  const handleCloseAck = status => {
    setShareToPass(status)
    setPassmemberAck(false)
  }

  const isTooYoung = () => {
    if (!dob) return false
    const diffYrs = DateTime.now().diff(DateTime.fromJSDate(dob), 'years').years
    return Math.abs(diffYrs) <= 15
  }

  const handleSubmitPage2 = event => {
    setErrors([])
    let errors = []

    if (!streetAddress?.trim() || !city?.trim() || !state?.trim() || !zip || !phoneNumber) {
      errors.push('Please enter all required fields.')
    }
    if (state === 'NY') {
      errors.push('Sorry, our service not currently providing in your state')
    } else if (!validZip) {
      errors.push('Please double check your zip code')
    } else if (phoneNumber && !validPhoneNumber(phoneNumber)) {
      errors.push('Invalid phone number format')
    }
    if (errors.length > 0) {
      setErrors(errors)
      return
    }
    setPage(3)
  }

  const handleSubmitPage1 = event => {
    event.preventDefault()
    setErrors([])
    let errors = []
    if (!gender) {
      errors.push('Please specify if vaginal swab would be appropriate!')
    }
    if (!pronoun?.trim() || !fname?.trim() || !lname?.trim() || !dob || !gender) {
      errors.push('Please enter required fields.')
    } else if (dob && isTooYoung()) {
      errors.push('The minimum age requirement of 15 to use our kits. If you have purchased one of our kits, please reach out to customer service to request a refund.')
    }
    if (errors.length > 0) {
      setErrors(errors)
      return
    }
    setPage(2)
  }

  const handleSubmitFinal = event => {
    setErrors([])
    let errors = []
    event.preventDefault()
    const prepPhoneNum = getPreparedPhoneNumber(phoneNumber)
    const payload = {
      ...patient,
      userId: patient.userId,
      email: patient.email,
      phoneNumber: prepPhoneNum?.startsWith('+1') ? prepPhoneNum : '+1' + prepPhoneNum,
      firstName: fname.trim(),
      lastName: lname.trim(),
      addresseeType: patient.addresseeType || 'PATIENT',
      gender: gender || sexAtBirth,
      genderAtBirth: sexAtBirth,
      currentGenderIdentity: genderIdentity,
      pronoun: pronouns || pronoun,
      preferPronoun: [pronoun || pronouns],
      dob: dob ? DateTime.fromJSDate(dob).toISODate() : '',
      address: {
        street_address: streetAddress,
        street_address2: streetAddress2,
        locality: city,
        region: state,
        postal_code: zip,
      },
      allergies: allergies || 'none',
      symptoms: symptoms || 'none',
      medication: medications || 'none',
      race: race,
      ethnicity: ethnicity,
      passMember: isPassMember,
      shareWithPass: shareToPass,
      preferredName: preferredName,
    }
    ApiService.updateUserHealthData(payload)
      .then(res => {
        setPatient(res.data)
        sessionStorage.setItem('h_prof', 'true')
        history.push(getNextPath())
      })
      .catch(err => {
        errors.push('Error saving your info, please try again!')
      })
    if (errors.length > 0) {
      setErrors(errors)
      return
    }
    // history.push(getNextPath())
  }

  const getNextPath = () => {
    if (!nextPath?.trim() || nextPath === '/') return '/'
    return nextPath.startsWith('/') ? nextPath : `/${nextPath}`
  }

  const displayMissingInfo = () => {
    if (!patient) return ''
    const missingInfo = []
    if (!patient.genderAtBirth) missingInfo.push('Sex at Birth')
    if (!patient.pronoun) missingInfo.push('Addressed as')
    if (!patient.currentGenderIdentity) missingInfo.push('Current Gender Identity')
    if (!patient.medication) missingInfo.push('Medications')
    if (missingInfo.length === 0) return ''
    return ` ${missingInfo.join(', ')} to avoid any delays in care.`
  }

  return (
    <div className="form" id="profile-form">
      <div className="form-body profileUpdate-body">
        {errors?.map((err, index) => {
          return (
            <div className="signup-errors" ref={index === 0 ? errorRef : null} key={index}>
              {err}
            </div>
          )
        })}
        <div class="row w-100-mobile">
          {isProfileIncomplete ? (
            <div
              style={{
                fontSize: '12px',
                fontWeight: '400',
                marginTop: '10px',
                marginBottom: '10px',
                lineHeight: '24px',
                textAlign: 'center',
                padding: '10px',
                backgroundColor: '#fff4e5',
                color: '#663c00',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid #ffcc80',
              }}
            >
              Hi there! It looks like your TBD Health patient profile is incomplete. Please select your {displayMissingInfo()}
            </div>
          ) : (
            <span className="updateProfile_text">UPDATE PROFILE</span>
          )}

          <form id="signupFormPage1">
            <div className="form-group">
              <label htmlFor="sexAtBirth" className="profile-label">
                Sex at Birth
              </label>
              <select
                id="sexAtBirth"
                value={sexAtBirth || ''}
                onChange={e => setSexAtBirth(e.target.value)}
                required
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '16px',
                  width: '100%',
                }}
              >
                <option value="">Select an option</option>
                {sexOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="addressedAs" className="profile-label">
                Addressed as
              </label>
              <select
                id="addressedAs"
                value={pronouns || ''}
                onChange={e => setPronouns(e.target.value)}
                required
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '16px',
                  width: '100%',
                }}
              >
                <option value="">Select an option</option>
                {addressedAtOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="genderIdentity" className="profile-label">
                Gender Identity
              </label>
              <select
                id="genderIdentity"
                value={genderIdentity || ''}
                onChange={e => setGenderIdentity(e.target.value)}
                required
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '16px',
                  width: '100%',
                }}
              >
                <option value="">Select an option</option>
                {identity.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label for="pronoun" className="profile-label">
                Please list any medications that you are currently taking.
              </label>
              <input
                type="text"
                id="medications"
                value={medications}
                onChange={e => setMedicationsValue(e.target.value)}
                placeholder="eg Tylenol, Advil, etc."
                required
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '16px',
                  width: '100%',
                }}
              />
            </div>
            <div id="profileButtonRow">
              <button form="signupFormPage1" type="submit" onClick={handleSubmitFinal} className="btn socialbutton-customizable" id="socialbutton-customizable">
                Confirm
              </button>
            </div>

            {/* Profile Summary */}
            <div className="profile-summary" style={{ marginTop: 24, marginBottom: 24, padding: 16, backgroundColor: '#f9f9f9', borderRadius: 12 }}>
              <p style={{ marginTop: '12px' }}>
                Wish to make any changes in these details?{' '}
                <Link
                  to="/settings"
                  style={{
                    color: '#1F7F61',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: '600',
                  }}
                >
                  Click here
                </Link>
                <br />
              </p>
              <div style={{ fontSize: '14px' }}>
                <p>
                  <strong>Name:</strong> {patient.firstName} {patient.lastName}
                </p>
                <p>
                  <strong>Email:</strong> {patient.email}
                </p>
                <p>
                  <strong>Phone:</strong> {patient.phoneNumber}
                </p>
                <p>
                  <strong>Date of Birth:</strong> {patient.dob}
                </p>
                <p>
                  <strong>Address:</strong> {patient.address.street_address}, {patient.address.locality}, {patient.address.region} {patient.address.postal_code}
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
export default HealthProfile
